import { useEffect, useMemo, useState } from 'react';
import {
  Calendar as CalendarIcon,
  Search,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Archive,
  ArrowLeftCircle,
  Wand2,
  MapPin,
  Clock,
  History,
  Trash2,
  Receipt,
  RefreshCcw,
  DownloadCloud,
  PoundSterling,
  KeyRound,
  X as XIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import EmptyState from '../components/EmptyState';
import ConvertTimeTreeModal from '../components/ConvertTimeTreeModal';

interface TTEventComment {
  author_id: number;
  text: string;
  created_at: string | null;
  is_tick: boolean;
}

interface TTXeroMatch {
  invoice_id?: string;
  invoice_number?: string;
  contact_name?: string;
  contact_id?: string;
  status?: string;
  total?: number;
  date?: string;
  reference?: string;
  score?: number;
  matched_at?: string;
}

interface TTEvent {
  timetree_uuid: string;
  title: string;
  note: string;
  location: string;
  all_day: boolean;
  start_at: string;
  end_at: string;
  label_id: number;
  label_color: string;
  label_name: string;
  imported: boolean;
  imported_at?: string | null;
  imported_job_id?: string | null;
  dismissed: boolean;
  dismissed_reason?: string | null;
  comments?: TTEventComment[];
  comment_count?: number;
  last_comment_text?: string;
  last_comment_at?: string | null;
  has_completion_marker?: boolean;
  strict_completion_marker?: boolean;
  tick_count?: number;
  xero_match?: TTXeroMatch;
  hours_logged?: number;
  hours_snippets?: string[];
  parts_logged?: { qty: number; description: string }[];
  parts_line_count?: number;
  est_value?: number;
  labour_estimate?: number;
  parts_estimate?: number;
}

interface Summary {
  total: number;
  imported: number;
  dismissed: number;
  pending: number;
  marked_done_pending?: number;
  xero_matched_pending?: number;
  labels: { id: number; color: string; name: string; count: number }[];
}

type Tab = 'pending' | 'dismissed' | 'imported';

const PAGE_SIZE = 30;

export default function TimeTreeImport() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [events, setEvents] = useState<TTEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pending');
  const [q, setQ] = useState('');
  const [labelId, setLabelId] = useState<number | null>(null);
  const [markedDone, setMarkedDone] = useState<boolean | null>(null);
  const [xeroMatched, setXeroMatched] = useState<boolean | null>(null);
  const [dateFrom, setDateFrom] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState<string>('');
  const [page, setPage] = useState(0);
  const [convertTarget, setConvertTarget] = useState<TTEvent | null>(null);
  const [sortByValue, setSortByValue] = useState(false);
  const [hasTTCreds, setHasTTCreds] = useState<boolean | null>(null);
  const [ttCredEmail, setTTCredEmail] = useState<string>('Andy@wilson-contractors.co.uk');
  const [pullOpen, setPullOpen] = useState(false);
  const [pulling, setPulling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sum, list] = await Promise.all([
        api.get('/timetree/summary'),
        api.get('/timetree/events', {
          params: {
            imported: tab === 'imported' ? true : false,
            dismissed: tab === 'dismissed' ? true : tab === 'pending' ? false : undefined,
            label_id: labelId ?? undefined,
            marked_done: markedDone ?? undefined,
            xero_matched: xeroMatched ?? undefined,
            q: q || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            sort: sortByValue ? 'value_desc' : undefined,
            skip: page * PAGE_SIZE,
            limit: PAGE_SIZE,
          },
        }),
      ]);
      setSummary(sum.data);
      setEvents(list.data.events);
      setTotal(list.data.total);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not load TimeTree events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, labelId, markedDone, xeroMatched, dateFrom, dateTo, page, sortByValue]);

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      load();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const dismiss = async (uuid: string) => {
    try {
      await api.post(`/timetree/events/${uuid}/dismiss`, { reason: 'Not a real job' });
      toast.success('Skipped');
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Could not skip');
    }
  };

  const undismiss = async (uuid: string) => {
    try {
      await api.post(`/timetree/events/${uuid}/undismiss`, {});
      toast.success('Restored');
      load();
    } catch {
      toast.error('Could not restore');
    }
  };

  const bulkSkipPast = async () => {
    if (!window.confirm('Skip ALL past events (already-done history)? You can restore any of them later from the Skipped tab.')) return;
    const t = toast.loading('Skipping past events…');
    try {
      const res = await api.post('/timetree/bulk-dismiss', { scope: 'past' });
      toast.success(`${res.data.dismissed} past events skipped`, { id: t });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Bulk skip failed', { id: t });
    }
  };

  const bulkSkipMarkedDone = async () => {
    const n = summary?.marked_done_pending || 0;
    if (!window.confirm(`Skip ${n} events where Sammy's last comment is "Done" / "job sheet done" / "invoice done"? All restorable later.`)) return;
    const t = toast.loading('Skipping marked-done events…');
    try {
      const res = await api.post('/timetree/bulk-dismiss', { scope: 'marked_done', reason: 'Skipped — Sammy marked job sheet done' });
      toast.success(`${res.data.dismissed} events skipped`, { id: t });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Bulk skip failed', { id: t });
    }
  };

  const bulkSkipXeroMatched = async () => {
    const n = summary?.xero_matched_pending || 0;
    if (!window.confirm(`Skip ${n} events that have an auto-matched Xero invoice? Please spot-check a few first — matches ≥ 0.9 are almost certainly correct, but 0.6-0.9 needs review.`)) return;
    const t = toast.loading('Skipping…');
    try {
      const res = await api.post('/timetree/bulk-dismiss', { scope: 'xero_matched', reason: 'Skipped — matched to a Xero invoice' });
      toast.success(`${res.data.dismissed} events skipped`, { id: t });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Bulk skip failed', { id: t });
    }
  };

  const runXeroCrossCheck = async () => {
    if (!window.confirm('Fetch the last 12 months of Xero invoices and try to match them against unbilled TimeTree events? Takes 30-60 seconds.')) return;
    const t = toast.loading('Cross-checking Xero (may take a minute)…');
    try {
      const res = await api.post('/timetree/xero-crosscheck?months_back=12&day_window=14');
      const d = res.data;
      toast.success(
        `${d.events_matched} matches from ${d.events_scanned} events (${d.invoices_pulled} invoices)`,
        { id: t, duration: 6000 }
      );
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Cross-check failed. Is Xero connected?', { id: t });
    }
  };

  const bulkSkipFiltered = async () => {
    if (!window.confirm(`Skip all ${total} events currently shown? You can restore any of them later.`)) return;
    const t = toast.loading('Skipping…');
    try {
      const res = await api.post('/timetree/bulk-dismiss', {
        scope: 'filtered',
        q: q || undefined,
        label_id: labelId ?? undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      toast.success(`${res.data.dismissed} events skipped`, { id: t });
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Bulk skip failed', { id: t });
    }
  };

  // Check whether TT credentials are on file (once)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/timetree/credentials');
        setHasTTCreds(!!res.data.has_credentials);
        if (res.data.email) setTTCredEmail(res.data.email);
      } catch {
        setHasTTCreds(false);
      }
    })();
  }, []);

  const runPullFromTimeTree = async (password?: string) => {
    setPulling(true);
    const t = toast.loading('Pulling from TimeTree — this takes 3-4 mins…', { duration: 300000 });
    try {
      const res = await api.post('/timetree/full-refresh', password ? { password } : {}, {
        timeout: 600000, // 10 mins
      });
      const d = res.data;
      toast.success(
        `Synced ${d.total_events} events · ${d.pending} pending · ${d.xero_matched} Xero-matched`,
        { id: t, duration: 8000 }
      );
      setSortByValue(true);      // auto sort by £ value desc after sync
      setPage(0);
      setTab('pending');
      setPullOpen(false);
      setHasTTCreds(true);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Sync failed. Check TimeTree password.', {
        id: t,
        duration: 8000,
      });
    } finally {
      setPulling(false);
    }
  };

  const handlePullClick = () => {
    if (hasTTCreds) {
      if (window.confirm('Pull the latest TimeTree data and cross-check against Xero? Takes 3-4 mins.')) {
        runPullFromTimeTree();
      }
    } else {
      setPullOpen(true);
    }
  };

  return (
    <div className="space-y-6" data-testid="timetree-import-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps text-brand-600">Migration</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">TimeTree Import</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Review every event from your TimeTree calendar. Convert the ones that are real jobs, skip the noise.
            Skipped items are kept — you can restore any of them later.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <button
            onClick={handlePullClick}
            disabled={pulling}
            className="btn btn-primary shadow-pop"
            data-testid="tt-pull-btn"
          >
            {pulling ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Pulling…
              </>
            ) : (
              <>
                <DownloadCloud className="h-4 w-4" /> Pull from TimeTree
              </>
            )}
          </button>
          {summary && (
            <div className="grid grid-cols-4 gap-2 text-center min-w-[280px]">
              <StatBubble label="Total" value={summary.total} tone="slate" />
              <StatBubble label="Pending" value={summary.pending} tone="amber" />
              <StatBubble label="Imported" value={summary.imported} tone="emerald" />
              <StatBubble label="Skipped" value={summary.dismissed} tone="rose" />
            </div>
          )}
        </div>
      </div>

      {/* Bulk actions — most useful for pruning the 5000+ past events already invoiced in Xero */}
      {tab === 'pending' && summary && summary.pending > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm font-semibold text-amber-900">Trim the list quickly</div>
            <p className="text-xs text-amber-800/80 mt-0.5">
              Bulk-skip events that Sammy already marked as complete in TimeTree comments, or all past events.
            </p>
          </div>
          {(summary.marked_done_pending || 0) > 0 && (
            <button
              onClick={bulkSkipMarkedDone}
              className="btn btn-secondary border-emerald-400 text-emerald-900 bg-white hover:bg-emerald-50"
              data-testid="tt-bulk-skip-marked-done"
            >
              <CheckCircle2 className="h-4 w-4" /> Skip {summary.marked_done_pending} marked done
            </button>
          )}
          {(summary.xero_matched_pending || 0) > 0 && (
            <button
              onClick={bulkSkipXeroMatched}
              className="btn btn-secondary border-sky-400 text-sky-900 bg-white hover:bg-sky-50"
              data-testid="tt-bulk-skip-xero"
            >
              <Receipt className="h-4 w-4" /> Skip {summary.xero_matched_pending} Xero-matched
            </button>
          )}
          <button
            onClick={runXeroCrossCheck}
            className="btn btn-secondary border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
            data-testid="tt-run-xero-crosscheck"
          >
            <RefreshCcw className="h-4 w-4" /> Cross-check Xero
          </button>
          <button
            onClick={bulkSkipPast}
            className="btn btn-secondary border-amber-300 text-amber-900 bg-white hover:bg-amber-50"
            data-testid="tt-bulk-skip-past"
          >
            <History className="h-4 w-4" /> Skip all past events
          </button>
          {total > 0 && (
            <button
              onClick={bulkSkipFiltered}
              className="btn btn-secondary border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
              data-testid="tt-bulk-skip-view"
            >
              <Trash2 className="h-4 w-4" /> Skip all {total} shown
            </button>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200">
        {(['pending', 'imported', 'dismissed'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t);
              setPage(0);
            }}
            className={`px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px capitalize ${
              tab === t
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
            data-testid={`tt-tab-${t}`}
          >
            {t === 'pending' ? 'To Review' : t === 'imported' ? 'Imported' : 'Skipped'}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Search title, notes, location…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="tt-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500">From</label>
          <input
            type="date"
            className="input w-40"
            value={dateFrom}
            onChange={(e) => {
              setPage(0);
              setDateFrom(e.target.value);
            }}
            data-testid="tt-date-from"
          />
          <label className="text-xs font-semibold text-slate-500">to</label>
          <input
            type="date"
            className="input w-40"
            value={dateTo}
            onChange={(e) => {
              setPage(0);
              setDateTo(e.target.value);
            }}
            data-testid="tt-date-to"
          />
        </div>
        <button
          onClick={() => {
            setPage(0);
            setSortByValue((v) => !v);
          }}
          className={`btn btn-sm ${
            sortByValue
              ? 'btn-primary'
              : 'btn-secondary border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
          title="Sort by estimated £ value (labour + parts) descending"
          data-testid="tt-sort-value-toggle"
        >
          <PoundSterling className="h-4 w-4" />
          {sortByValue ? 'Sorted by £ value' : 'Sort by £ value'}
        </button>
      </div>

      {/* Label chips + marked-done filter */}
      {summary && (summary.labels.length > 1 || (summary.marked_done_pending || 0) > 0) && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setLabelId(null);
              setMarkedDone(null);
              setXeroMatched(null);
              setPage(0);
            }}
            className={`chip transition ${
              labelId === null && markedDone === null && xeroMatched === null
                ? 'bg-brand-600 text-white ring-brand-600'
                : 'bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200'
            }`}
          >
            All ({summary.total})
          </button>
          {(summary.marked_done_pending || 0) > 0 && (
            <button
              onClick={() => {
                setMarkedDone(markedDone === true ? null : true);
                setPage(0);
              }}
              className={`chip transition ${
                markedDone === true
                  ? 'bg-emerald-600 text-white ring-emerald-600'
                  : 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100'
              }`}
              data-testid="tt-filter-marked-done"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Marked done ({summary.marked_done_pending})
            </button>
          )}
          {(summary.xero_matched_pending || 0) > 0 && (
            <button
              onClick={() => {
                setXeroMatched(xeroMatched === true ? null : true);
                setPage(0);
              }}
              className={`chip transition ${
                xeroMatched === true
                  ? 'bg-sky-600 text-white ring-sky-600'
                  : 'bg-sky-50 text-sky-800 ring-sky-200 hover:bg-sky-100'
              }`}
              data-testid="tt-filter-xero-matched"
            >
              <Receipt className="h-3.5 w-3.5" />
              Xero-matched ({summary.xero_matched_pending})
            </button>
          )}
          {summary.labels.map((l) => (
            <button
              key={l.id}
              onClick={() => {
                setLabelId(labelId === l.id ? null : l.id);
                setPage(0);
              }}
              className={`chip transition ${
                labelId === l.id ? 'ring-2 ring-offset-1 ring-brand-600' : 'ring-slate-200'
              }`}
              style={{ backgroundColor: `${l.color}22`, color: l.color }}
              data-testid={`tt-label-${l.id}`}
            >
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
              {l.name || `Label ${l.id}`} ({l.count})
            </button>
          ))}
        </div>
      )}

      {/* Event list */}
      {loading ? (
        <div className="py-16 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : events.length === 0 ? (
        <EmptyState
          icon={CalendarIcon}
          title="Nothing here"
          subtitle={
            tab === 'pending'
              ? 'No pending events match your filters. Try widening the date range.'
              : tab === 'imported'
              ? 'No events imported yet.'
              : 'No skipped events.'
          }
        />
      ) : (
        <div className="grid gap-3">
          {events.map((ev) => (
            <EventRow
              key={ev.timetree_uuid}
              ev={ev}
              tab={tab}
              onConvert={() => setConvertTarget(ev)}
              onDismiss={() => dismiss(ev.timetree_uuid)}
              onRestore={() => undismiss(ev.timetree_uuid)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-slate-600 pt-4">
          <div>
            Showing {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)} of {total}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn btn-secondary btn-sm"
              data-testid="tt-prev-page"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <span className="text-xs font-mono text-slate-500">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
              className="btn btn-secondary btn-sm"
              data-testid="tt-next-page"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Convert modal */}
      {convertTarget && (
        <ConvertTimeTreeModal
          event={convertTarget}
          onClose={() => setConvertTarget(null)}
          onDone={() => {
            setConvertTarget(null);
            load();
          }}
        />
      )}

      {/* Pull-from-TimeTree password modal (first time only, then remembered) */}
      {pullOpen && (
        <PullFromTimeTreeModal
          defaultEmail={ttCredEmail}
          submitting={pulling}
          onClose={() => setPullOpen(false)}
          onSubmit={async (email, password, calendarCode) => {
            // Save credentials once, then trigger the sync
            try {
              await api.post('/timetree/credentials', {
                email,
                password,
                calendar_code: calendarCode || undefined,
              });
              setHasTTCreds(true);
              setTTCredEmail(email);
            } catch (e: any) {
              toast.error(e?.response?.data?.detail || 'Could not save credentials');
              return;
            }
            await runPullFromTimeTree(password);
          }}
        />
      )}
    </div>
  );
}

function EventRow({
  ev,
  tab,
  onConvert,
  onDismiss,
  onRestore,
}: {
  ev: TTEvent;
  tab: Tab;
  onConvert: () => void;
  onDismiss: () => void;
  onRestore: () => void;
}) {
  const startFmt = useMemo(() => {
    if (!ev.start_at) return '';
    try {
      const d = parseISO(ev.start_at);
      return ev.all_day ? format(d, 'EEE d MMM yyyy') : format(d, 'EEE d MMM · HH:mm');
    } catch {
      return ev.start_at;
    }
  }, [ev.start_at, ev.all_day]);

  return (
    <article
      className="card p-4 sm:p-5 flex flex-wrap items-start gap-4 hover:shadow-pop transition"
      data-testid={`tt-row-${ev.timetree_uuid}`}
    >
      {/* Colour swatch */}
      <div
        className="w-1.5 self-stretch rounded-full shrink-0"
        style={{ backgroundColor: ev.label_color || '#94a3b8' }}
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-bold text-slate-900 truncate">
            {ev.title || '(no title)'}
          </h3>
          {ev.strict_completion_marker && (
            <span className="chip bg-emerald-100 text-emerald-800 ring-emerald-200" data-testid={`tt-badge-done-${ev.timetree_uuid}`}>
              <CheckCircle2 className="h-3 w-3" /> Marked done
            </span>
          )}
          {ev.xero_match && (
            <span
              className={`chip ${
                (ev.xero_match.score ?? 0) >= 0.9
                  ? 'bg-sky-100 text-sky-900 ring-sky-300'
                  : 'bg-sky-50 text-sky-800 ring-sky-200'
              }`}
              title={`Score ${ev.xero_match.score} — auto-matched to Xero invoice ${ev.xero_match.invoice_number}`}
              data-testid={`tt-badge-xero-${ev.timetree_uuid}`}
            >
              <Receipt className="h-3 w-3" />
              Xero {ev.xero_match.invoice_number} · £{(ev.xero_match.total ?? 0).toFixed(2)}
              {(ev.xero_match.score ?? 0) < 0.9 && <span className="opacity-70">?</span>}
            </span>
          )}
          {(ev.hours_logged || 0) > 0 && (
            <span
              className="chip bg-indigo-50 text-indigo-800 ring-indigo-200"
              title={`Logged hours from note/comments${ev.hours_snippets?.length ? ': ' + ev.hours_snippets.join(', ') : ''}`}
              data-testid={`tt-badge-hours-${ev.timetree_uuid}`}
            >
              <Clock className="h-3 w-3" /> {ev.hours_logged} hr{(ev.hours_logged || 0) === 1 ? '' : 's'}
            </span>
          )}
          {(ev.parts_line_count || 0) > 0 && (
            <span
              className="chip bg-purple-50 text-purple-800 ring-purple-200"
              title={ev.parts_logged?.map((p) => `${p.qty}x ${p.description}`).join('\n')}
              data-testid={`tt-badge-parts-${ev.timetree_uuid}`}
            >
              <Wand2 className="h-3 w-3" /> {ev.parts_line_count} part{(ev.parts_line_count || 0) === 1 ? '' : 's'}
            </span>
          )}
          {(ev.est_value || 0) > 0 && (
            <span
              className="chip bg-amber-50 text-amber-900 ring-amber-200 font-bold"
              title={`Labour £${(ev.labour_estimate ?? 0).toFixed(2)} + Parts £${(ev.parts_estimate ?? 0).toFixed(2)}`}
              data-testid={`tt-badge-value-${ev.timetree_uuid}`}
            >
              £{(ev.est_value ?? 0).toFixed(2)}
            </span>
          )}
          {ev.imported && (
            <span className="chip bg-emerald-100 text-emerald-800 ring-emerald-200">
              <CheckCircle2 className="h-3 w-3" /> Imported
            </span>
          )}
          {ev.dismissed && !ev.imported && (
            <span className="chip bg-rose-100 text-rose-800 ring-rose-200">
              <Archive className="h-3 w-3" /> Skipped
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap text-sm text-slate-500 mt-1">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {startFmt}
          </span>
          {ev.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" /> {ev.location}
            </span>
          )}
        </div>
        {ev.note && (
          <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap break-words">{ev.note}</p>
        )}
        {ev.comments && ev.comments.filter((c) => c.text).length > 0 && (
          <div className="mt-3 pl-3 border-l-2 border-slate-200 space-y-1.5">
            <div className="label-caps text-slate-400">
              Comments ({ev.comments.filter((c) => c.text).length})
              {ev.tick_count ? ` · ${ev.tick_count} ticks` : ''}
            </div>
            {ev.comments
              .filter((c) => c.text)
              .map((c, i, arr) => {
                const isLast = i === arr.length - 1;
                const isDoneMarker = isLast && ev.strict_completion_marker;
                return (
                  <div key={i} className="text-sm">
                    <span className="text-slate-400 text-xs font-mono mr-1.5">
                      {c.created_at ? format(parseISO(c.created_at), 'd MMM ’’yy') : ''}
                    </span>
                    <span
                      className={`whitespace-pre-wrap break-words ${
                        isDoneMarker ? 'font-bold text-emerald-700' : 'text-slate-700'
                      }`}
                    >
                      {c.text}
                    </span>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 shrink-0 self-center">
        {tab === 'pending' && (
          <>
            <button
              onClick={onDismiss}
              className="btn btn-secondary"
              title="Skip — not a real job"
              data-testid={`tt-dismiss-${ev.timetree_uuid}`}
            >
              <XCircle className="h-4 w-4" /> Skip
            </button>
            <button
              onClick={onConvert}
              className="btn btn-primary"
              data-testid={`tt-convert-${ev.timetree_uuid}`}
            >
              <Wand2 className="h-4 w-4" /> Convert to Job
            </button>
          </>
        )}
        {tab === 'dismissed' && (
          <button onClick={onRestore} className="btn btn-secondary" data-testid={`tt-restore-${ev.timetree_uuid}`}>
            <ArrowLeftCircle className="h-4 w-4" /> Restore
          </button>
        )}
        {tab === 'imported' && ev.imported_job_id && (
          <a href={`/jobs/${ev.imported_job_id}`} className="btn btn-secondary">
            View job →
          </a>
        )}
      </div>
    </article>
  );
}

function StatBubble({ label, value, tone }: { label: string; value: number; tone: 'slate' | 'amber' | 'emerald' | 'rose' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-700',
    amber: 'bg-amber-100 text-amber-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    rose: 'bg-rose-100 text-rose-800',
  } as const;
  return (
    <div className={`rounded-lg px-2 py-2 ${tones[tone]}`}>
      <div className="text-xl font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase font-semibold tracking-wide mt-1">{label}</div>
    </div>
  );
}

function PullFromTimeTreeModal({
  defaultEmail,
  submitting,
  onClose,
  onSubmit,
}: {
  defaultEmail: string;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (email: string, password: string, calendarCode: string) => Promise<void> | void;
}) {
  const [email, setEmail] = useState(defaultEmail || 'Andy@wilson-contractors.co.uk');
  const [password, setPassword] = useState('');
  const [calendarCode, setCalendarCode] = useState('qSCXxiPogQZc');

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      data-testid="tt-pull-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-pop max-w-lg w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          disabled={submitting}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          data-testid="tt-pull-modal-close"
        >
          <XIcon className="h-5 w-5" />
        </button>
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-brand-50 text-brand-700 rounded-lg">
            <KeyRound className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">Pull from TimeTree</h2>
            <p className="text-sm text-slate-500 mt-1">
              We&apos;ll sign into TimeTree, pull every event + comment, then cross-check them against
              Xero invoices. Takes 3-4 minutes. Credentials are saved encrypted so you only enter them once.
            </p>
          </div>
        </div>
        <form
          className="mt-5 space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!password) return;
            await onSubmit(email.trim(), password, calendarCode.trim());
          }}
        >
          <div>
            <label className="label-caps text-slate-500">TimeTree email</label>
            <input
              type="email"
              className="input mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              autoComplete="username"
              data-testid="tt-pull-modal-email"
              required
            />
          </div>
          <div>
            <label className="label-caps text-slate-500">TimeTree password</label>
            <input
              type="password"
              className="input mt-1"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              autoComplete="current-password"
              placeholder="Enter your TimeTree password"
              data-testid="tt-pull-modal-password"
              required
              autoFocus
            />
          </div>
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer hover:text-slate-700">Advanced — calendar code</summary>
            <input
              type="text"
              className="input mt-2 font-mono text-xs"
              value={calendarCode}
              onChange={(e) => setCalendarCode(e.target.value)}
              disabled={submitting}
              data-testid="tt-pull-modal-calendar"
            />
          </details>
          <div className="pt-3 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="btn btn-secondary"
              data-testid="tt-pull-modal-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !password}
              className="btn btn-primary"
              data-testid="tt-pull-modal-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Syncing (3-4 mins)…
                </>
              ) : (
                <>
                  <DownloadCloud className="h-4 w-4" /> Save &amp; Pull now
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
